/**
 * Authentication configuration
 * Supports multiple authentication providers
 * Uses runtime configuration from backend for production deployment
 */

import { getRuntimeConfig } from './runtime.config';

export enum AuthProvider {
    LOCAL = 'local',
    AZURE_AD = 'azure-ad',
}

export interface AuthConfig {
    provider: AuthProvider;
    useMockAzureAD: boolean;
    azureAd?: {
        clientId: string;
        authority: string;
        redirectUri: string;
        scopes: string[];
    };
}

/**
 * Get authentication configuration from runtime config or environment
 *
 * Priority:
 *   1. Runtime config from backend (/server/config.js)
 *   2. Build-time environment variables
 *   3. Default values
 */
export const getAuthConfig = (): AuthConfig => {
    const runtimeCfg = getRuntimeConfig();
    const provider = (import.meta.env.VITE_AUTH_PROVIDER || 'local') as AuthProvider;
    const useMockAzureAD = import.meta.env.VITE_USE_MOCK_AZURE_AD === 'true';

    const config: AuthConfig = {
        provider,
        useMockAzureAD,
    };

    // Azure AD configuration - only validate if not using mock
    if (provider === AuthProvider.AZURE_AD && !useMockAzureAD) {
        config.azureAd = {
            // Use runtime config (from backend) with fallback to build-time env vars
            clientId: runtimeCfg.azureClientId || import.meta.env.VITE_AZURE_CLIENT_ID || '',
            authority: runtimeCfg.azureAuthority || import.meta.env.VITE_AZURE_AUTHORITY || '',
            redirectUri: runtimeCfg.redirectUri || import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
            scopes: (import.meta.env.VITE_AZURE_SCOPES || 'User.Read').split(','),
        };

        // Validate Azure AD config
        if (!config.azureAd.clientId || !config.azureAd.authority) {
            console.error('❌ Azure AD configuration is incomplete');
            throw new Error('Azure AD configuration is missing required fields');
        }
    }

    const mode = useMockAzureAD ? 'Mock Azure AD' : provider === AuthProvider.AZURE_AD ? 'Real Azure AD' : 'Local';
    console.log(`✅ Auth provider initialized: ${mode}`);

    return config;
};

export const authConfig = getAuthConfig();
